import { useCallback, useEffect, useMemo, useState } from 'react'
import { shallowEqual } from 'react-redux'

import { addDays, chunkRange, formatDayShort, formatMonthShort, type LoadPart } from '@krp/ui-kit'
import { onecCashSummary, type CashSummary, type CashSummaryArgs, type CashSummaryResult } from '@krp/resource-onec-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import type { RootState } from '../../app/types'
import { localToday } from './supplyDates'

type ChunkResult = ReturnType<ReturnType<typeof onecCashSummary.select>>

/** Сколько месяцев из скольких прочитано; `null` — читать больше нечего (всё прочитано или остались только неудавшиеся). */
export interface SummaryProgress {
  done: number
  total: number
}

export interface ChunkedSummary {
  result: CashSummaryResult | null
  progress: SummaryProgress | null
  /** Состояние каждого месяца периода: ждёт очереди, читается, прочитан, не прочитан. */
  parts: readonly LoadPart[]
  /** Перечитать только неудавшиеся месяцы. */
  retryFailed: () => void
  /** Сегодняшний день перечитывается («Обновить»), прочитанное прежде остаётся на экране. */
  todayRefreshing: boolean
}

const NO_PARTS: readonly LoadPart[] = []
const NOTHING = (): void => undefined
const NO_TIMINGS: ReadonlyMap<string, Timing> = new Map()

interface Timing {
  startedAt: number
  elapsedMs?: number
}

/** Быстрее этого — ответ пришёл из кэша браузера, а не из службы. */
const CACHED_BELOW_MS = 40

const SECONDS_FORMAT = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 })

/**
 * Статистика чеков 1С за период любой длины: период режется на куски по календарному месяцу, куски читаются ПО ОДНОМУ (живая 1С
 * не получает двенадцать запросов разом) и склеиваются. Прочитанное показывается сразу, не дожидаясь остальных, а `parts` говорит о
 * каждом месяце отдельно: ждёт очереди, читается, прочитан (сколько секунд или «из кэша»), не прочитан (текст отказа).
 * Отказ части месяцев не прячет остальные: итоги показываются неполными, а неудавшиеся месяцы перечитываются кнопкой. Отказ всех — отказ с
 * текстом. Кэш — на каждый кусок свой: смена места, кассира и вида запросов не порождает, а перелистывание периодов не перечитывает
 * прошлые месяцы; месяц с сегодняшним днём перечитывается при каждом входе в период.
 */
export function useChunkedSummary(args: CashSummaryArgs | null): ChunkedSummary {
  const dispatch = useAppDispatch()
  const key = args === null ? '' : `${args.resourceId}|${args.from}|${args.to}|${args.fresh ?? 0}`
  const chunks = useMemo<readonly CashSummaryArgs[]>(
    () => (args === null ? [] : chunksOf(args, localToday())),
    // Ключ — те же значения: объект args у вызывающего может быть новым, а период тем же.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key],
  )
  // Сколько читался каждый кусок: запись приходит, когда кусок прочитан, и относится к своему периоду (`key`).
  const [recorded, setRecorded] = useState<{ key: string; map: ReadonlyMap<string, Timing> }>({ key: '', map: NO_TIMINGS })

  useEffect(() => {
    let cancelled = false
    const today = localToday()
    const subscriptions: { unsubscribe: () => void }[] = []
    void (async () => {
      for (const chunk of chunks) {
        if (cancelled) return
        const startedAt = Date.now()
        // Прошлые месяцы уже не меняются — берутся из кэша; месяц с сегодняшним днём перечитывается при каждом входе в период:
        // за это время могли появиться новые чеки.
        const subscription = dispatch(onecCashSummary.initiate(chunk, { forceRefetch: chunk.to >= today }))
        subscriptions.push(subscription)
        // Следующий кусок — когда этот прочитан (или отказал): живая 1С не получает всё сразу.
        await subscription.then(
          () => undefined,
          () => undefined,
        )
        const timing: Timing = { startedAt, elapsedMs: Date.now() - startedAt }
        if (!cancelled) setRecorded((previous) => ({ key, map: new Map(previous.key === key ? previous.map : NO_TIMINGS).set(chunk.from, timing) }))
      }
    })()
    return () => {
      cancelled = true
      for (const subscription of subscriptions) subscription.unsubscribe()
    }
  }, [chunks, dispatch, key])

  const results = useAppSelector(
    (state: RootState): readonly ChunkResult[] => chunks.map((chunk) => onecCashSummary.select(chunk)(state)),
    shallowEqual,
  )

  const retryFailed = useCallback(() => {
    void (async () => {
      for (const [index, chunk] of chunks.entries()) {
        if (results[index]?.isError !== true) continue
        const subscription = dispatch(onecCashSummary.initiate(chunk, { forceRefetch: true }))
        await subscription.then(
          () => undefined,
          () => undefined,
        )
        subscription.unsubscribe()
      }
    })()
  }, [chunks, results, dispatch])

  if (args === null || chunks.length === 0) return { result: null, progress: null, parts: NO_PARTS, retryFailed: NOTHING, todayRefreshing: false }
  // Куски читаются по одному: «читается» — первый ещё не законченный, остальные ждут очереди.
  const timings = new Map(recorded.key === key ? recorded.map : NO_TIMINGS)
  const reading = chunks[results.findIndex((result) => !result.isSuccess && !result.isError)]
  if (reading !== undefined && !timings.has(reading.from)) timings.set(reading.from, { startedAt: 0 })
  const today = localToday()
  const todayRefreshing = chunks.some((chunk, index) => chunk.from === today && results[index]?.data !== undefined && results[index].isLoading)
  return { ...merge(args, results), parts: partsOf(chunks, results, timings), retryFailed, todayRefreshing }
}

/**
 * Куски периода: календарные месяцы, а сегодняшний день — отдельным куском. Прошлые дни уже не меняются и берутся из кэша;
 * «Обновить» (`fresh`) уходит только с сегодняшним куском — 1С перечитывает один день, а не месяц или год.
 */
export function chunksOf(args: CashSummaryArgs, today: string): readonly CashSummaryArgs[] {
  return chunkRange(args.from, args.to, 31).flatMap((range) => {
    const base = { resourceId: args.resourceId }
    if (range.to < today || range.from > today) return [{ ...base, ...range }]
    const current = { ...base, from: today, to: range.to, ...(args.fresh !== undefined ? { fresh: args.fresh } : {}) }
    return range.from < today ? [{ ...base, from: range.from, to: addDays(today, -1) }, current] : [current]
  })
}

/** Текст отказа куска: у ошибки службы есть `message`; иначе общая фраза. */
function failureText(result: ChunkResult): string {
  const error: unknown = result.error
  if (typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string' && error.message !== '') return error.message
  return 'не прочитан'
}

export function partsOf(chunks: readonly CashSummaryArgs[], results: readonly ChunkResult[], timings: ReadonlyMap<string, Timing>): readonly LoadPart[] {
  return chunks.map((chunk, index): LoadPart => {
    const result = results[index]
    const timing = timings.get(chunk.from)
    // Кусок не с первого числа (сегодняшний день, начало периода) подписан своими днями, а не месяцем.
    const label = chunk.from.endsWith('-01') ? `${formatMonthShort(chunk.from)} ${chunk.from.slice(0, 4)}` : `${formatDayShort(chunk.from)} – ${formatDayShort(chunk.to)}`
    if (result === undefined || result.isUninitialized) return { key: chunk.from, label, state: 'pending' }
    if (result.isError) return { key: chunk.from, label, state: 'failed', detail: failureText(result) }
    if (result.isSuccess && result.data !== undefined) {
      const elapsed = timing?.elapsedMs
      const detail = elapsed === undefined ? null : elapsed < CACHED_BELOW_MS ? 'из кэша' : `${SECONDS_FORMAT.format(elapsed / 1000)} с`
      return { key: chunk.from, label, state: 'done', detail }
    }
    // Не начато (цепочка идёт по одному) — ждёт; начато и без ответа — читается.
    return { key: chunk.from, label, state: timing === undefined ? 'pending' : 'running' }
  })
}

export function merge(args: CashSummaryArgs, results: readonly ChunkResult[]): Pick<ChunkedSummary, 'result' | 'progress'> {
  // Кусок, который перечитывается («Обновить»), остаётся в итогах со старыми данными до ответа.
  const ready = results.flatMap((result) => (result.data !== undefined ? [result.data] : []))
  const outstanding = results.filter((result) => result.data === undefined && !result.isError).length
  const failed = results.find((result) => result.isError)
  const first = ready[0]
  if (first === undefined) {
    // Ничего не прочитано: пока идёт очередь — загрузка, когда очередь кончилась и всё отказало — отказ с текстом первого.
    if (failed !== undefined && outstanding === 0) {
      return {
        result: { isUninitialized: false, isLoading: false, isError: true, isSuccess: false, ...(failed.error !== undefined ? { error: failed.error } : {}) },
        progress: null,
      }
    }
    return { result: { isUninitialized: false, isLoading: true, isError: false, isSuccess: false }, progress: null }
  }
  const cashiers = new Map<string, string>()
  for (const envelope of ready) for (const cashier of envelope.data.cashiers) cashiers.set(cashier.key, cashier.name)
  const merged: CashSummary = {
    from: args.from,
    to: args.to,
    rows: ready.flatMap((envelope) => envelope.data.rows),
    cashiers: [...cashiers.entries()].map(([key, name]) => ({ key, name })),
    truncated: ready.some((envelope) => envelope.data.truncated),
    observedAt: ready.map((envelope) => envelope.data.observedAt).sort().at(-1) ?? first.data.observedAt,
  }
  return {
    result: {
      isUninitialized: false,
      isLoading: false,
      isError: false,
      isSuccess: true,
      data: { resourceId: first.resourceId, configurationVersion: first.configurationVersion, data: merged },
    },
    progress: outstanding > 0 ? { done: ready.length, total: results.length } : null,
  }
}
